import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useMatchEventsSocket } from '../../hooks/useMatchEventsSocket';
import { useMatchEventState } from '../../api/useMatchEvents';
import { secondsToClock, pickFresherState } from '../../utils/matchEventsFeed';
import {
  formatStateLine,
  groupBySubgroup,
  type SniperSubgroup,
} from '../../utils/sniperArm';
import {
  useActivateSniperEvent,
  useDeactivateSniperEvent,
  usePatchSniperMarket,
  useDeleteSniperMarket,
  useBulkPatchSniperMarkets,
} from '../../api/useSniper';
import type { SniperEvent } from '../../types/sniper.types';
import { SniperMarketRow } from './SniperMarketRow';
import { SniperBulkBar, type SniperBulkPatch } from './SniperBulkBar';
import { SniperImportDialog } from './SniperImportDialog';
import { SeedScoreControl } from '../matchEvents/SeedScoreControl';
import { VarReviewBanner } from '../matchEvents/VarReviewBanner';

/**
 * One event (match) card: the live state line (score + `Corners H–A` + clock),
 * an Active toggle (reuses the `/match-events` feed active set), an import-by-link
 * control, and the event's markets subgrouped by trigger type (Gol / Corner).
 *
 * The score/corners/clock/VAR come from the BACKEND-authored `MatchState`
 * (`match_state` WS event + `GET /match-events/state/:fixtureId` backfill) — the
 * FE renders it and derives nothing. The live WS value supersedes the REST
 * backfill; the same `MatchState` drives each row's ARMED badge.
 */
export function SniperEventCard({ event }: { event: SniperEvent }) {
  const [importOpen, setImportOpen] = useState(false);

  const activate = useActivateSniperEvent();
  const deactivate = useDeactivateSniperEvent();
  const patchMarket = usePatchSniperMarket();
  const deleteMarket = useDeleteSniperMarket();

  // Only an ACTIVE event streams — subscribing to an inactive fixture joins a
  // room with no feed attached.
  const subscribeFixtureId = event.active ? event.fixtureId : null;
  const { matchState } = useMatchEventsSocket({
    fixtureIds: subscribeFixtureId != null ? [subscribeFixtureId] : [],
  });
  const { data: backfill } = useMatchEventState(subscribeFixtureId);

  // Backend-authored MatchState: prefer whichever of {live WS, REST backfill}
  // carries the NEWER `ts` — so a stale live value can't permanently shadow a
  // fresher REST snapshot recovered after a reconnect. The FE RENDERS this — it
  // never derives score/corners itself.
  const state = useMemo(() => {
    if (!event.active || subscribeFixtureId == null) return null;
    const live = matchState.get(subscribeFixtureId);
    return pickFresherState(live, backfill);
  }, [event.active, subscribeFixtureId, matchState, backfill]);

  const clockText = secondsToClock(state?.clock ?? null);
  // No backend MatchState for an ACTIVE fixture = the baseline has not been
  // established yet (feed cold-started mid-match). Show an explicit "score
  // syncing…" so the operator knows WHY the board is blank and that a seed is
  // available — NEVER a fabricated 0–0. An inactive event just shows the teams.
  const syncing = event.active && !state;
  const stateLine = state
    ? formatStateLine(state, event.homeTeam, event.awayTeam, clockText)
    : syncing
      ? `${event.homeTeam} vs ${event.awayTeam} · score syncing…`
      : `${event.homeTeam} vs ${event.awayTeam}`;

  const subgroups = useMemo(
    () => groupBySubgroup(event.markets ?? []),
    [event.markets],
  );
  const allMarketIds = useMemo(
    () => (event.markets ?? []).map((m) => m._id),
    [event.markets],
  );

  const bulkPatch = useBulkPatchSniperMarkets();

  // Collapse (per match + per event-type subgroup) and bulk-selection state.
  const [collapsed, setCollapsed] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<SniperSubgroup>>(
    new Set(),
  );
  // Deliberately allowed to hold tombstones (ids of markets since deleted) —
  // every read goes through the `allMarketIds` filter below, so a stale id can
  // never reach the wire.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Rows with uncommitted param edits, reported up by each row (drafts live in
  // the row). Returning `prev` unchanged keeps this from re-rendering in a loop.
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());

  const handleDirtyChange = useCallback((marketId: string, isDirty: boolean) => {
    setDirtyIds((prev) => {
      if (isDirty === prev.has(marketId)) return prev;
      const next = new Set(prev);
      if (isDirty) next.add(marketId);
      else next.delete(marketId);
      return next;
    });
  }, []);

  const toggleGroupCollapsed = (sg: SniperSubgroup) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(sg)) next.delete(sg);
      else next.add(sg);
      return next;
    });

  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const setManySelected = (ids: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });

  // Selection is scoped to THIS card; filter against live ids so a deleted
  // market can't linger in the outgoing bulk request.
  const selectedIds = allMarketIds.filter((id) => selected.has(id));
  const selectedCount = selectedIds.length;
  const bulkBusy =
    bulkPatch.isLoading || patchMarket.isLoading || deleteMarket.isLoading;

  // Errors surface through the global mutation-error toast (the mutation carries
  // a registered `mutationKey`, so the message names the partial-write risk) —
  // no local toast, which would double-report.
  const runBulk = (patch: SniperBulkPatch, onApplied?: () => void) => {
    if (selectedIds.length === 0) return;
    bulkPatch.mutate({ marketIds: selectedIds, patch }, { onSuccess: onApplied });
  };

  // Bulk-ENABLING arms N markets to fire real orders on the next trigger, and the
  // selection can include rows hidden behind a collapsed subgroup — confirm it.
  // It also writes ONLY {enabled}, so a selected row with uncommitted param edits
  // would go live at its OLD values while the row still displays the new ones:
  // the same footgun the per-row switch guards, which cannot see the selection.
  const setBulkEnabled = (enabled: boolean) => {
    if (selectedIds.length === 0) return;
    if (enabled) {
      const unsaved = selectedIds.filter((id) => dirtyIds.has(id)).length;
      if (unsaved > 0) {
        toast.error(
          `${unsaved} selected market(s) have unsaved param edits — confirm (✓) or revert them before enabling.`,
        );
        return;
      }
      if (
        !window.confirm(
          `Enable ${selectedIds.length} market(s)? They will fire on the next trigger.`,
        )
      ) {
        return;
      }
    }
    runBulk({ enabled });
  };

  const toggleActive = (next: boolean) => {
    if (next) activate.mutate(event.fixtureId);
    else deactivate.mutate(event.fixtureId);
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      {/* `useFlexGap` gives a wrapped row a real ROW-gap (MUI's default
          margin-left spacing leaves line 2 flush against line 1). It also drops
          MUI's `margin: 0` child reset, so FormControlLabel's built-in
          `-11px/16px` margins have to be neutralised explicitly — otherwise the
          Active switch sits off the right edge on EVERY card.
          The right-hand controls are their own Stack pinned with `ml: 'auto'`:
          a zero-basis `flex: 1` spacer is always packed onto line 1 by the flex
          line-breaking algorithm, so on wrap it left-aligns the controls and the
          Active kill-switch jumps across the card. An auto margin survives the
          wrap and keeps them right-aligned on whichever line they land. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        <Tooltip title={collapsed ? 'Expand match' : 'Collapse match'}>
          <IconButton
            size="small"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'expand match' : 'collapse match'}
            sx={{ p: 0.25 }}
          >
            {collapsed ? <ChevronRightIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>
        {allMarketIds.length > 0 && (
          <Tooltip title="Select all markets in this match">
            <Checkbox
              size="small"
              checked={selectedCount === allMarketIds.length}
              indeterminate={selectedCount > 0 && selectedCount < allMarketIds.length}
              onChange={(e) => setManySelected(allMarketIds, e.target.checked)}
              inputProps={{ 'aria-label': 'select all markets in match' }}
              sx={{ p: 0.25 }}
            />
          </Tooltip>
        )}
        <Typography variant="subtitle2" sx={{ color: 'common.white', fontWeight: 600 }}>
          {stateLine}
        </Typography>
        {event.league && (
          <Chip size="small" variant="outlined" label={event.league} sx={{ height: 20, fontSize: 11 }} />
        )}
        {event.live && <Chip size="small" color="success" label="live" sx={{ height: 20, fontSize: 11 }} />}
        {/* ALWAYS mounted, never gated at the call site — the banner keeps a
            persistent aria-live region whose TEXT toggles; inserting the region
            together with its content would silently void the announcement. */}
        <VarReviewBanner active={!!state?.lastVarActive} />
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          sx={{ ml: 'auto', '& > .MuiFormControlLabel-root': { m: 0 } }}
        >
          <Button
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => setImportOpen(true)}
            sx={{ fontSize: 12 }}
          >
            Import markets
          </Button>
          <FormControlLabel
            // Margins are reset by the parent Stack's child selector above.
            sx={{ '& .MuiFormControlLabel-label': { fontSize: 12 } }}
            control={
              <Switch
                size="small"
                checked={event.active}
                onChange={(e) => toggleActive(e.target.checked)}
                inputProps={{ 'aria-label': 'activate event feed' }}
              />
            }
            label={event.active ? 'Active' : 'Inactive'}
          />
        </Stack>
      </Stack>

      <Collapse in={!collapsed}>
        {/* Operator seed: establish the baseline when it is syncing, or correct an
            existing one (the feed can mis-state a score — override allowed). The
            engine re-publishes the authoritative MatchState; the board renders
            from that, never from the typed values. */}
        {event.active && (
          <Box sx={{ mt: 0.75 }}>
            <SeedScoreControl
              fixtureId={event.fixtureId}
              homeTeam={event.homeTeam}
              awayTeam={event.awayTeam}
              mode={state ? 'override' : 'seed'}
            />
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        {selectedCount > 0 && (
          <Box sx={{ mb: 1 }}>
            <SniperBulkBar
              count={selectedCount}
              busy={bulkBusy}
              onApplyParams={runBulk}
              onSetEnabled={setBulkEnabled}
              onClear={() => setSelected(new Set())}
            />
          </Box>
        )}

        {subgroups.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No markets yet — use “Import markets” to add some.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {subgroups.map((group) => {
              const groupCollapsed = collapsedGroups.has(group.subgroup);
              const ids = group.rows.map((r) => r._id);
              const nSel = ids.filter((id) => selected.has(id)).length;
              return (
                <Box key={group.subgroup}>
                  {/* Event-type header: collapse + select-all-of-type + label + count. */}
                  <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mb: 0.25 }}>
                    <IconButton
                      size="small"
                      onClick={() => toggleGroupCollapsed(group.subgroup)}
                      aria-label={groupCollapsed ? `expand ${group.subgroup}` : `collapse ${group.subgroup}`}
                      sx={{ p: 0.25 }}
                    >
                      {groupCollapsed ? (
                        <ChevronRightIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                    <Tooltip title={`Select all ${group.subgroup}`}>
                      <Checkbox
                        size="small"
                        checked={nSel === ids.length}
                        indeterminate={nSel > 0 && nSel < ids.length}
                        onChange={(e) => setManySelected(ids, e.target.checked)}
                        inputProps={{ 'aria-label': `select all ${group.subgroup}` }}
                        sx={{ p: 0.25 }}
                      />
                    </Tooltip>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ lineHeight: 1.4 }}
                    >
                      {group.label} · {ids.length}
                    </Typography>
                    {/* Selection stays visible when the subgroup is folded, so a
                        bulk action can never hit rows the operator can't see. */}
                    {nSel > 0 && (
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={`${nSel} sel.`}
                        sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }}
                      />
                    )}
                  </Stack>
                  <Collapse in={!groupCollapsed}>
                    <Stack spacing={0.5}>
                      {group.rows.map((market) => (
                        <SniperMarketRow
                          key={market._id}
                          market={market}
                          state={state}
                          busy={
                            patchMarket.isLoading ||
                            deleteMarket.isLoading ||
                            bulkPatch.isLoading
                          }
                          onPatch={(patch) =>
                            patchMarket.mutate({ marketId: market._id, patch })
                          }
                          onDelete={() => deleteMarket.mutate(market._id)}
                          selected={selected.has(market._id)}
                          onToggleSelected={() => toggleSelected(market._id)}
                          onDirtyChange={handleDirtyChange}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        )}
      </Collapse>

      <SniperImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        fixtureId={event.fixtureId}
        homeTeam={event.homeTeam}
        awayTeam={event.awayTeam}
      />
    </Paper>
  );
}
